import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useUserProfile } from '@/context/UserProfileContext';
import { ProfileCompletionModal } from './ProfileCompletionModal';
import { ParentWhatsAppModal } from './ParentWhatsAppModal';
import { getActivePortal, getUserData, isAuthenticated } from '@/utils/auth';

export function ProfileCompletionCheck() {
  const [showModal, setShowModal] = useState(false);
  const [showParentPhoneModal, setShowParentPhoneModal] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const { profile, loading } = useUserProfile();
  const location = useLocation();

  useEffect(() => {
    // If dismissed in this session or if it is the teacher portal, don't show
    if (dismissed || getActivePortal() === 'teacher') return;

    // Only show on main dashboard/entry pages
    const dashboardPages = [
      '/student/learn', 
      '/teacher/ai-tools',
      '/student/dashboard',
      '/teacher/teach',
      '/guardian/dashboard'
    ];
    
    const isDashboard = dashboardPages.some(page => location.pathname.startsWith(page));
    if (!isDashboard) {
      if (showModal) setShowModal(false);
      if (showParentPhoneModal) setShowParentPhoneModal(false);
      return;
    }

    // Wait until profile is loaded and loading is finished
    if (loading || !profile) return;

    // Must be authenticated
    const portal = getActivePortal();
    if (!isAuthenticated(portal)) return;

    const freshUserData = getUserData(portal);
    
    const isActuallyComplete = (p) => {
        if (!p) return false;
        // If custom curriculum preview user, consider profile complete
        if (p.is_custom_curriculum) return true;
        // If backend explicitly says it's complete, trust it
        if (p.is_profile_complete) return true;
        
        // Guardians are complete if they have full_name AND phone_no filled in
        if (p.user_type === 'guardian') {
            return !!(p.full_name && p.full_name.trim() && p.phone_no && p.phone_no.trim());
        }
        
        // Otherwise use fields check
        return !!(p.full_name && 
                 p.user_type && 
                 (p.board?.id || p.board) && // Check if board is object or ID
                 (p.user_type === 'teacher' || (p.grade?.id || p.grade)));
    };

    const isComplete = isActuallyComplete(profile) || isActuallyComplete(freshUserData);

    if (!isComplete) {
      if (!showModal) setShowModal(true);
      if (showParentPhoneModal) setShowParentPhoneModal(false);
      return;
    }

    if (showModal) {
      setShowModal(false);
    }

    // Now check for existing student missing parent WhatsApp number
    const userType = profile.user_type || freshUserData?.user_type;
    const isStudent = portal === 'student' && userType === 'student';
    
    if (isStudent) {
      const hasPhone = Boolean(
        (profile.phone_no && String(profile.phone_no).trim()) ||
        (profile.parent_whatsapp_number && String(profile.parent_whatsapp_number).trim()) ||
        (freshUserData?.phone_no && String(freshUserData.phone_no).trim()) ||
        (freshUserData?.parent_whatsapp_number && String(freshUserData.parent_whatsapp_number).trim())
      );

      const hasGraceSession = typeof window !== 'undefined' && window.sessionStorage?.getItem('parent_phone_grace_used') === 'true';

      if (!hasPhone && !hasGraceSession) {
        if (!showParentPhoneModal) setShowParentPhoneModal(true);
      } else {
        if (showParentPhoneModal) setShowParentPhoneModal(false);
      }
    } else {
      if (showParentPhoneModal) setShowParentPhoneModal(false);
    }
  }, [location.pathname, profile, loading, showModal, showParentPhoneModal, dismissed]);

  const handleComplete = () => {
    setShowModal(false);
    window.location.reload();
  };

  const handleParentPhoneComplete = () => {
    setShowParentPhoneModal(false);
    window.location.reload();
  };

  const handleClose = () => {
    setShowModal(false);
    setDismissed(true); // Don't show again in this app session
  };

  return (
    <>
      <ProfileCompletionModal
        isOpen={showModal}
        onComplete={handleComplete}
        onClose={handleClose}
        currentProfile={profile}
      />
      <ParentWhatsAppModal
        isOpen={showParentPhoneModal}
        onComplete={handleParentPhoneComplete}
        currentProfile={profile}
      />
    </>
  );
}
